import type { Chapter, Ledger, StoryBible } from '../../types';
import { StoryMode, ChapterLength } from '../../types';

export const FIXTURE_PROJECT_ID = 'fixture-project-normal-ch1';
export const FIXTURE_RUN_ID = 'fixture-run-normal-ch1';

export const FIXTURE_CHAPTER_CONTENT =
  'The supply cart groaned against the frozen mud as Mara hauled the last crate uphill. ' +
  'She checked the strap on her sidearm twice before the patrol crossed the ridge. ' +
  'By nightfall the weapon was gone and only ash marked where the cart had stalled.';

export const FIXTURE_CHAPTER_TITLE = 'First Light on the Ridge';

export const FIXTURE_LEDGER: Ledger = {
  characters: {
    mara: { name: 'Mara', status: 'Active', location: 'Northern Ridge' },
  },
  locations: {
    ridge: { name: 'Northern Ridge', status: 'Active' },
  },
  inventory: ['field journal'],
  plot_points: ['Reach the relay station'],
  resolved_plot_points: [],
  momentum: 'Rising',
  arc_stage: 'SETUP',
  current_time: 'Day 1, dawn',
  last_updated: '2026-01-01T00:00:00.000Z',
};

export const FIXTURE_STORY_BIBLE: StoryBible = {
  title: 'Fixture Chronicle',
  genre: [{ name: 'Fantasy', weight: 'primary' }],
  premise: {
    surface: 'A scout must cross hostile terrain.',
    tension: 'Every delay costs lives.',
    change: 'Safety cannot be restored.',
  },
  style_guide: 'Close third, grounded sensory detail.',
  mode: StoryMode.NOVEL,
  targetChapterCount: 12,
  chapterLength: ChapterLength.STANDARD,
  isClosingMode: false,
  volume: 1,
  chapterPlans: [
    {
      id: 'ch1',
      chapterNumber: 1,
      title: FIXTURE_CHAPTER_TITLE,
      summary: 'Mara moves the cart and loses her weapon.',
      requiredBeats: ['Disable the cart', 'Lose the sidearm'],
      status: 'planned',
      updatedAt: '2026-01-01T00:00:00.000Z',
      characterFocus: ['Mara'],
      locationFocus: ['Northern Ridge'],
      continuityNotes: [],
      forbiddenMoves: [],
    },
  ],
};

export const FIXTURE_UPDATED_LEDGER: Ledger = {
  ...FIXTURE_LEDGER,
  inventory: ['field journal', 'broken cart wheel'],
  plot_points: ['Investigate the missing sidearm'],
  resolved_plot_points: ['Reach the relay station'],
  momentum: 'Tightening',
  arc_stage: 'SETUP',
  characters: {
    mara: { name: 'Mara', status: 'Active', location: 'Northern Ridge', notes: 'Disarmed' },
  },
  latestStateDelta: {
    gained: [],
    lost: ['sidearm'],
    damaged: ['supply cart'],
    revealed: [],
    relationshipsChanged: [],
    goalsChanged: ['Secure the relay'],
    threatsEscalated: ['Unknown raiders'],
    unresolvedConsequences: ['Weapon missing'],
  },
};

export const FIXTURE_SCRIBE_RESPONSE = JSON.stringify({
  title: FIXTURE_CHAPTER_TITLE,
  scenes: [FIXTURE_CHAPTER_CONTENT],
  summary: 'Mara hauls the cart uphill and loses her weapon by nightfall.',
});

export const FIXTURE_INSPECTOR_RESPONSE = JSON.stringify({
  continuity: {
    status: 'PASS',
    reason: 'No continuity drift detected.',
    correction_order: '',
  },
  progression: {
    passed: true,
    goalPresent: true,
    activeOppositionPresent: true,
    protagonistMakesDecision: true,
    meaningfulCostPresent: true,
    permanentChangePresent: true,
    externalArcAdvanced: true,
    internalArcPressured: true,
    nextChapterPressureCreated: true,
    statusQuoResetDetected: false,
    correctionOrder: '',
  },
  density: {
    actionDecisionBeats: 2,
    conflictEscalationBeats: 1,
    emotionalChangeBeats: 1,
    consequenceBeats: 1,
    descriptiveOnlyParagraphs: 0,
    repeatedDetailSignals: [],
  },
});

export const FIXTURE_EDITOR_RESPONSE = JSON.stringify({
  status: 'APPROVED',
  feedback: 'Tone and pacing are sound.',
  polished_scenes: [FIXTURE_CHAPTER_CONTENT],
});

export const FIXTURE_PUBLICATION_RESPONSE = JSON.stringify({
  patched_scenes: [FIXTURE_CHAPTER_CONTENT],
  change_log: [],
  lore_compliance: [{ rail: 'Canon', status: 'PASS', note: 'Aligned with ledger.' }],
});

export const FIXTURE_ARCHIVIST_RESPONSE = JSON.stringify({
  characters: FIXTURE_UPDATED_LEDGER.characters,
  locations: FIXTURE_UPDATED_LEDGER.locations,
  inventory: FIXTURE_UPDATED_LEDGER.inventory,
  plot_points: FIXTURE_UPDATED_LEDGER.plot_points,
  resolved_plot_points: FIXTURE_UPDATED_LEDGER.resolved_plot_points,
  momentum: FIXTURE_UPDATED_LEDGER.momentum,
  arc_stage: FIXTURE_UPDATED_LEDGER.arc_stage,
  latestStateDelta: FIXTURE_UPDATED_LEDGER.latestStateDelta,
});

export const FIXTURE_MOCK_RESPONSES = [
  { agent: 'SCRIBE' as const, text: FIXTURE_SCRIBE_RESPONSE },
  { agent: 'INSPECTOR' as const, text: FIXTURE_INSPECTOR_RESPONSE },
  { agent: 'EDITOR' as const, text: FIXTURE_EDITOR_RESPONSE },
  { agent: 'PUBLICATION_EDITOR' as const, text: FIXTURE_PUBLICATION_RESPONSE },
  { agent: 'ARCHIVIST' as const, text: FIXTURE_ARCHIVIST_RESPONSE },
];

export function buildFixtureProjectState() {
  return {
    projectId: FIXTURE_PROJECT_ID,
    storyBible: structuredClone(FIXTURE_STORY_BIBLE),
    ledger: structuredClone(FIXTURE_LEDGER),
    chapters: [] as Chapter[],
    currentChapterNumber: 1,
  };
}

export interface ComparableChapterRunSnapshot {
  chapterTitle: string;
  chapterContent: string;
  inspectorStatus: string;
  correctionCount: number;
  editorStatus: string;
  publicationContent: string;
  ledgerCharacters: Record<string, unknown>;
  ledgerLocations: Record<string, unknown>;
  ledgerInventory: string[];
  plotPoints: string[];
  resolvedPlotPoints: string[];
  momentum: string;
  arcStage: string;
  nextChapterNumber: number;
}

export function toComparableSnapshot(input: {
  chapter: Chapter;
  ledger: Ledger;
  inspectorStatus: string;
  correctionCount: number;
  editorStatus: string;
  publicationContent: string;
  nextChapterNumber: number;
}): ComparableChapterRunSnapshot {
  return {
    chapterTitle: input.chapter.title || '',
    chapterContent: input.chapter.content || '',
    inspectorStatus: input.inspectorStatus,
    correctionCount: input.correctionCount,
    editorStatus: input.editorStatus,
    publicationContent: input.publicationContent,
    ledgerCharacters: input.ledger.characters || {},
    ledgerLocations: input.ledger.locations || {},
    ledgerInventory: [...(input.ledger.inventory || [])],
    plotPoints: [...(input.ledger.plot_points || [])],
    resolvedPlotPoints: [...(input.ledger.resolved_plot_points || [])],
    momentum: input.ledger.momentum,
    arcStage: input.ledger.arc_stage,
    nextChapterNumber: input.nextChapterNumber,
  };
}
